import { Directive, EventEmitter, Injectable, Input, Output } from "@angular/core";

@Injectable()
export class LpsConversionDetails {
    
    // 0080-00FF Latin-1 Supplemental, 0100-017F Latin Extended-A, 0180-024F Latin Extended-B, 0250-02AF IPA Extensions
    private static LATINUNICODE = LpsConversionDetails.generateRangeForUnicodeVariables(0x00C0, 0x02AF); // "\xC0-\xD6\xD8-\xF6\xF8-\xFF";
    
    // Remaining Unicode character blocks excluding ranges containing punctuation, box drawing, block elements, dingbats etc.
    private static GLOBALUNICODE = LpsConversionDetails.generateRangeForUnicodeVariables(0x0370, 0x1FFF) +
        LpsConversionDetails.generateRangeForUnicodeVariables(0x2C60, 0x2DDF) +
        LpsConversionDetails.generateRangeForUnicodeVariables(0x2E80, 0x2FDF) +
        LpsConversionDetails.generateRangeForUnicodeVariables(0x3040, 0xFFEF);
    
    //private static readonly globalUnicodeFieldInputDataRefRegExTemplate = '^((\\/(!)|\\/)?(?=[a-zA-Z0-9!LATINUNICODE!!GLOBALUNICODE!])([a-zA-Z0-9!LATINUNICODE!!GLOBALUNICODE!]|([ _\\-]+[a-zA-Z0-9!LATINUNICODE!!GLOBALUNICODE!]))+(\\/(?=[a-zA-Z0-9!LATINUNICODE!!GLOBALUNICODE!])([a-zA-Z0-9!LATINUNICODE!!GLOBALUNICODE!]|([ _\\-]+([a-zA-Z0-9!LATINUNICODE!!GLOBALUNICODE!]))+)*|\\3)*)?$';
    private static readonly globalUnicodeFieldInputFolderNameRegExTemplate = '^((?=[a-zA-Z0-9\\(\\)\\[\\]&\\+,;~!LATINUNICODE!!GLOBALUNICODE!])([a-zA-Z0-9\\(\\)\\[\\]&\\+,;~!LATINUNICODE!!GLOBALUNICODE!]|([ _\\-\\.`"\']+[a-zA-Z0-9\\(\\)\\[\\]&\\+,;~!LATINUNICODE!!GLOBALUNICODE!]))+)$'

    public static readonly fieldInputFolderNameRegEx: RegExp = LpsConversionDetails.buildMultiLingualFieldNameUsingCharCodesRegEx(LpsConversionDetails.globalUnicodeFieldInputFolderNameRegExTemplate);
    public static readonly invalidProjectIdCharsRegExp: RegExp = /[^\w]/gi;
    public static readonly invalidPortCharsRegExp: RegExp = /[^\d]/gi;
    
    private static buildMultiLingualFieldNameUsingCharCodesRegEx(masterRegularExpression: string, regExFlags: string = ''): RegExp {
        const regExpStr: string = this.buildMultiLingualRegExpressionStringUsingCharCodes(masterRegularExpression);
        let regExp: RegExp = new RegExp(regExpStr);
        if (regExFlags != null && regExFlags.length > 0) {
            regExp = new RegExp(regExpStr, regExFlags);
        }
        return regExp;
    }

    private static buildMultiLingualRegExpressionStringUsingCharCodes(masterRegularExpressionTemplate: string): string {
        let regExpStr: string = masterRegularExpressionTemplate;
        let replacePattern: RegExp = new RegExp('!LATINUNICODE!', 'gi');
        regExpStr = regExpStr.replace(replacePattern, LpsConversionDetails.LATINUNICODE);
        replacePattern = new RegExp('!GLOBALUNICODE!', 'gi');
        regExpStr = regExpStr.replace(replacePattern, LpsConversionDetails.GLOBALUNICODE);
        return regExpStr;
    }

    private static generateRangeForUnicodeVariables(rangeStart: any, rangeEnd: any = null): string {
        if (rangeEnd === null) {
            return String.fromCharCode(rangeStart);
        }
        return String.fromCharCode(rangeStart) + '-' + String.fromCharCode(rangeEnd);
    }

    public advancedFieldsInitialized: boolean = false;

    @Output() conversionDetailsChange = new EventEmitter<LpsConversionDetails>();
    @Output() advancedOptionsChange = new EventEmitter<LpsConversionDetails>();
    @Output() coreFieldChange = new EventEmitter<LpsConversionDetails>();

    @Output() spectrumInstanceTypeChange = new EventEmitter<string>();
    @Output() subProjectIdChange = new EventEmitter<string|undefined>();
    @Output() spectrumServerChange = new EventEmitter<string|undefined>();
    @Output() spectrumUserChange = new EventEmitter<string|undefined>();
    @Output() spectrumPassChange = new EventEmitter<string|undefined>();

    @Output() dropImageExtensionsChange = new EventEmitter<boolean>();
    @Output() imagesParentNameChange = new EventEmitter<string|undefined>();
    @Output() layoutsParentNameChange = new EventEmitter<string|undefined>();
    @Output() dataServicesParentNameChange = new EventEmitter<string|undefined>();
    @Output() createVersionsChange = new EventEmitter<boolean>();
    @Output() usePrinterNumberChange = new EventEmitter<boolean>();
    @Output() adjustUnsupportedDpiChange = new EventEmitter<boolean>();
    @Output() truncateDataChange = new EventEmitter<boolean>();
    @Output() jdbcUserChange = new EventEmitter<string|undefined>();
    @Output() jdbcPassChange = new EventEmitter<string|undefined>();
    @Output() jdbcUrlChange = new EventEmitter<string|undefined>();
    @Output() useLpsSchemaChange = new EventEmitter<boolean>();
    @Output() schemaOverrideChange = new EventEmitter<string>();
    @Output() inheritJdbcChange = new EventEmitter<boolean>();
    @Output() advOptionsSyncedChange = new EventEmitter<boolean>();
    @Output() advOptionsOverrideDpiChange = new EventEmitter<boolean>();
    

    @Output() migrationTypeChange = new EventEmitter<string>();
    @Output() outputChange = new EventEmitter<string>();
    @Output() targetVersionChange = new EventEmitter<string>();

    // Conversion Details

    public _spectrumCloud: boolean;
    public _subProjectId: string | undefined;
    public _spectrumServer: string | undefined;
    public _spectrumUser: string | undefined;
    public _spectrumPass: string | undefined;
    
    // Advanced Options

    public _dropImageExtensions: boolean;
    public _imagesParentName: string | undefined;
    public _layoutsParentName: string | undefined;
    public _dataServicesParentName: string | undefined;
    public _createVersions: boolean;
    public _usePrinterNumber: boolean;
    public _adjustUnsupportedDpi: boolean;
    public _truncateData: boolean;
    public _jdbcUser: string | undefined;
    public _jdbcPass: string | undefined;
    public _jdbcUrl: string | undefined;
    public _useLpsSchema: boolean;
    public _schemaOverride: string | undefined;
    public _inheritJdbc: boolean;
    public _advOptionsSynced: boolean;
    public _advOptionsOverrideDpi: boolean;

    // Core fields

    public _migrationType: string | undefined;
    public _output: string | undefined;
    public _targetVersion: string | undefined;

    public filename: string | undefined;
    public bucket: string = '';
    public email: string = '';

    @Input()
    public set spectrumInstanceType(value: string) {
        this._spectrumCloud = (value === 'Cloud' ? true : false);
        this.spectrumInstanceTypeChange.emit(value);
        this.conversionDetailsChange.emit(this);
    }
    public get spectrumInstanceType(): string { return this._spectrumCloud === true ? 'Cloud' : 'On Prem'; }
    public get spectrumCloud(): boolean { return this._spectrumCloud; }

    @Input()
    public set subProjectId(value: string | undefined) {
        if (value) { value.replace(LpsConversionDetails.invalidProjectIdCharsRegExp, ''); }
        this._subProjectId = value;
        this.subProjectIdChange.emit(value);
        this.conversionDetailsChange.emit(this);
    }
    public get subProjectId(): string | undefined { return this._subProjectId; }

    @Input()
    public set spectrumServer(value: string | undefined) {
        this._spectrumServer = value;
        this.spectrumServerChange.emit(value);
        this.conversionDetailsChange.emit(this);
    }
    public get spectrumServer(): string | undefined { return this._spectrumServer; }

    @Input()
    public set spectrumUser(value: string | undefined) {
        this._spectrumUser = value;
        this.spectrumUserChange.emit(value);
        this.conversionDetailsChange.emit(this);
    }
    public get spectrumUser(): string | undefined { return this._spectrumUser; }

    @Input()
    public set spectrumPass(value: string | undefined) {
        this._spectrumPass = value;
        this.spectrumPassChange.emit(value);
        this.conversionDetailsChange.emit(this);
    }
    public get spectrumPass(): string | undefined { return this._spectrumPass; }

    constructor() {
        this._spectrumCloud = false;
        this._subProjectId = '';
    }

    public initializeAdvancedFields() {
        if (this.spectrumInstanceType === 'On Prem') {
            this._imagesParentName = '0/Images/User Images';
            this._layoutsParentName = '0/Layouts';
            this._dataServicesParentName = '0/DataServices';
        } else {
            this._imagesParentName = undefined;
            this._layoutsParentName = undefined;
            this._dataServicesParentName = undefined;
        }
        this._dropImageExtensions = false;
        this._usePrinterNumber = false;
        this._adjustUnsupportedDpi = false;
        this._truncateData = false;
        this._useLpsSchema = false;
        this._inheritJdbc = true;
        this._advOptionsSynced = false;
        this._advOptionsOverrideDpi = false;
        this._jdbcUser = '';
        this._jdbcPass = '';
        this._jdbcUrl = '';
        this._schemaOverride = '';
        this.advancedFieldsInitialized = true;
    }

    public resetConversionFieldsOnInstanceTypeChange() {
        if (this.spectrumInstanceType === 'On Prem') {
            this._subProjectId = '';
            this._spectrumServer = undefined;
            this._spectrumUser = undefined;
            this._spectrumPass = undefined;
            if (this.advancedFieldsInitialized) {
                this._imagesParentName = '0/Images/User Images';
                this._layoutsParentName = '0/Layouts';
                this._dataServicesParentName = '0/DataServices';
            }
        } else {
            this._subProjectId = '';
            this._spectrumServer = '';
            this._spectrumUser = '';
            this._spectrumPass = '';
            this._createVersions = true;
            if (this.advancedFieldsInitialized) {
                this._imagesParentName = undefined;
                this._layoutsParentName =undefined;
                this._dataServicesParentName = undefined;
            }
        }
        this.conversionDetailsChange.emit(this);
    }

    @Input()
    public set dropImageExtensions(value: boolean) {
        this._dropImageExtensions = value;
        this.dropImageExtensionsChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get dropImageExtensions(): boolean { return this._dropImageExtensions; }

    @Input()
    public set createVersions(value: boolean) {
        this._createVersions = value;
        this.createVersionsChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get createVersions(): boolean { return this._createVersions; }

    @Input()
    public set usePrinterNumber(value: boolean) {
        this._usePrinterNumber = value;
        this.usePrinterNumberChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get usePrinterNumber(): boolean { return this._usePrinterNumber; }

    @Input()
    public set adjustUnsupportedDpi(value: boolean) {
        this._adjustUnsupportedDpi = value;
        this.adjustUnsupportedDpiChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get adjustUnsupportedDpi(): boolean { return this._adjustUnsupportedDpi; }

    @Input()
    public set truncateData(value: boolean) {
        this._truncateData = value;
        this.truncateDataChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get truncateData(): boolean { return this._truncateData; }

    @Input()
    public set useLpsSchema(value: boolean) {
        this._useLpsSchema = value;
        this.useLpsSchemaChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get useLpsSchema(): boolean { return this._useLpsSchema; }

    @Input()
    public set inheritJdbc(value: boolean) {
        this._inheritJdbc = value;
        this.inheritJdbcChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get inheritJdbc(): boolean { return this._inheritJdbc; }

    @Input()
    public set advOptionsSynced(value: boolean) {
        this._advOptionsSynced = value;
        this.advOptionsSyncedChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get advOptionsSynced(): boolean { return this._advOptionsSynced }
	
	@Input()
    public set advOptionsOverrideDpi(value: boolean) {
        this._advOptionsOverrideDpi = value;
        this.advOptionsOverrideDpiChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get advOptionsOverrideDpi(): boolean { return this._advOptionsOverrideDpi }

    @Input()
    public set imagesParentName(value: string | undefined) {
        this._imagesParentName = value;
        this.imagesParentNameChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get imagesParentName(): string | undefined { return this._imagesParentName; }

    @Input()
    public set layoutsParentName(value: string | undefined) {
        this._layoutsParentName = value;
        this.layoutsParentNameChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get layoutsParentName(): string | undefined { return this._layoutsParentName; }

    @Input()
    public set dataServicesParentName(value: string | undefined) {
        this._dataServicesParentName = value;
        this.dataServicesParentNameChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get dataServicesParentName(): string | undefined { return this._dataServicesParentName; }

    @Input()
    public set jdbcUser(value: string | undefined) {
        this._jdbcUser = value;
        this.jdbcUserChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get jdbcUser(): string | undefined { return this._jdbcUser; }

    @Input()
    public set jdbcPass(value: string | undefined) {
        this._jdbcPass = value;
        this.jdbcPassChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get jdbcPass(): string | undefined { return this._jdbcPass; }

    @Input()
    public set jdbcUrl(value: string | undefined) {
        this._jdbcUrl = value;
        this.jdbcUrlChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get jdbcUrl(): string | undefined { return this._jdbcUrl; }

    @Input()
    public set schemaOverride(value: string | undefined) {
        this._schemaOverride = value;
        this.schemaOverrideChange.emit(value);
        this.advancedOptionsChange.emit(this);
    }
    public get schemaOverride(): string | undefined { return this._schemaOverride; }

    @Input()
    public set migrationType(value: string | undefined) {
        this._migrationType = value;
        this.migrationTypeChange.emit(value);
        this.coreFieldChange.emit(this);
    }
    public get migrationType(): string | undefined { return this._migrationType; }

    @Input()
    public set output(value: string | undefined) {
        this._output = value;
        this.outputChange.emit(value);
        this.coreFieldChange.emit(this);
    }
    public get output(): string | undefined { return this._output; }

    @Input()
    public set targetVersion(value: string | undefined) {
        this._targetVersion = value;
        this.targetVersionChange.emit(value);
        this.coreFieldChange.emit(this);
    }
    public get targetVersion(): string | undefined { return this._targetVersion; }

    public get invalidSubProjectId(): boolean {
        return (this.spectrumInstanceType === 'On Prem' && (this._subProjectId === undefined || this._subProjectId === null || this._subProjectId.length === 0));
    }

    public get invalidUrl(): boolean {
        return (this.spectrumInstanceType === 'Cloud' && (this._spectrumServer === undefined || this._spectrumServer === null || this._spectrumServer.length === 0));
    }

    public get invalidUserName(): boolean {
        return (this.spectrumInstanceType === 'Cloud' && (this._spectrumUser === undefined || this._spectrumUser === null || this._spectrumUser.length === 0));
    }

    public get invalidPassword(): boolean {
        return (this.spectrumInstanceType === 'Cloud' && (this._spectrumPass === undefined || this._spectrumPass === null || this._spectrumPass.length === 0));
    }

    public get invalidTargetFolder(): boolean {
        const valueDNE: boolean = (this.spectrumInstanceType === 'Cloud' && (this._subProjectId === undefined || this._subProjectId === null || this._subProjectId.length === 0));
        if (this._subProjectId && !(valueDNE)) {
            const regExpMatches: RegExpMatchArray | null = this._subProjectId.match(LpsConversionDetails.fieldInputFolderNameRegEx);
            return (regExpMatches === null || (regExpMatches && regExpMatches.length === 0));
        }
        return true;
    }

    public get invalidImagesParentName(): boolean {
        return (this._imagesParentName === undefined || this._imagesParentName === null || this._imagesParentName.length === 0);
    }

    public get invalidLayoutsParentName(): boolean {
        return (this._layoutsParentName === undefined || this._layoutsParentName === null || this._layoutsParentName.length === 0);
    }

    public get invalidDataServicesParentName(): boolean {
        return (this._dataServicesParentName === undefined || this._dataServicesParentName === null || this._dataServicesParentName.length === 0);
    }

    public validateConversionFields(): boolean {
        let isValid = false;
        if (this.spectrumInstanceType === 'On Prem') {
            isValid = (!this.invalidSubProjectId);
        } else if (this.spectrumInstanceType === 'Cloud') {
            isValid = ((!this.invalidUrl) && (!this.invalidUserName) && (!this.invalidPassword) && (!this.invalidTargetFolder));
        }
        return isValid;
    }

    public validateAdvancedOptionFields(): boolean {
        if (this.spectrumInstanceType === 'Cloud') { return true; }
        return ((!this.invalidImagesParentName) && (!this.invalidLayoutsParentName) && (!this.invalidDataServicesParentName));
    }

}